import { useQualityCertificateApprovalsStore } from '@/stores'
import { createPrefetchGuard } from '@/utils/routerGuards'
import Quality from '@/views/Quality/Index.vue'

function prefetchCertificateApprovalLookups({ papers = true, certificates = false } = {}) {
    const certificateApprovalsStore = useQualityCertificateApprovalsStore()
    const requests = []

    if (papers && !certificateApprovalsStore.papers.length) {
        requests.push(certificateApprovalsStore.fetchCertificateApprovalPapers().catch(() => null))
    }

    if (certificates && !certificateApprovalsStore.paperCertificates.length) {
        requests.push(certificateApprovalsStore.fetchPaperCertificates().catch(() => null))
    }

    return Promise.all(requests)
}

export default [
    {
        path: '/quality',
        name: 'quality',
        redirect: { name: 'qualityComplaintsList' },
        component: Quality,
        meta: {
            title: 'quality.menuTitle',
            menuRendered: true,
            menuTitle: 'quality.menuTitle',
            permission: ['complaints', 'certificate_approvals'],
            icon: 'mdi-message-alert'
        },
        children: [
            {
                path: 'complaints',
                name: 'qualityComplaintsList',
                component: () => import('@/views/Quality/Complaints/Index.vue'),
                meta: {
                    title: 'quality.complaints.pageTitle',
                    menuRendered: true,
                    menuTitle: 'quality.complaints.menuTitle',
                    requiresAuth: true,
                    permission: 'complaints'
                }
            },
            {
                path: 'complaints/external/create',
                name: 'qualityComplaintsExternalCreate',
                component: () => import('@/views/Quality/Complaints/External/Index.vue'),
                meta: {
                    title: 'quality.complaints.external.create.pageTitle',
                    requiresAuth: true,
                    permission: 'complaints_create'
                }
            },
            {
                path: 'complaints/external/:id',
                name: 'qualityComplaintsExternalEdit',
                component: () => import('@/views/Quality/Complaints/External/Index.vue'),
                meta: {
                    title: 'quality.complaints.external.edit.pageTitle',
                    requiresAuth: true,
                    permission: 'complaints'
                }
            },
            {
                path: 'complaints/internal/create',
                name: 'qualityComplaintsInternalCreate',
                component: () => import('@/views/Quality/Complaints/Internal/Index.vue'),
                meta: {
                    title: 'quality.complaints.internal.create.pageTitle',
                    requiresAuth: true,
                    permission: ['internal_complaints_basic_create', 'internal_complaints_create']
                }
            },
            {
                path: 'complaints/internal/:id',
                name: 'qualityComplaintsInternalEdit',
                component: () => import('@/views/Quality/Complaints/Internal/Index.vue'),
                meta: {
                    title: 'quality.complaints.internal.edit.pageTitle',
                    requiresAuth: true,
                    permission: ['internal_complaints_basic_update', 'internal_complaints_update', 'complaints_confirm_update']
                }
            },
            {
                path: 'storage-complaints',
                name: 'qualityStorageComplaints',
                component: () => import('@/views/Quality/StorageComplaints/Index.vue'),
                meta: {
                    title: 'quality.storageComplaints.pageTitle',
                    menuRendered: true,
                    menuTitle: 'quality.storageComplaints.menuTitle',
                    requiresAuth: true,
                    permission: 'complaints_warehouse'
                }
            },
            {
                path: 'qualitative-notes',
                name: 'qualityQualitativeNotes',
                component: () => import('@/views/Quality/QualitativeNotes/Index.vue'),
                meta: {
                    title: 'quality.qualitativeNotes.pageTitle',
                    menuRendered: true,
                    menuTitle: 'quality.qualitativeNotes.menuTitle',
                    requiresAuth: true,
                    permission: 'qualitative_notes'
                }
            },
            {
                path: 'compliances',
                name: 'qualityCodeCompliance',
                component: () => import('@/views/Quality/CodeCompliance/Index.vue'),
                meta: {
                    title: 'quality.codeCompliance.pageTitle',
                    menuRendered: true,
                    menuTitle: 'quality.codeCompliance.menuTitle',
                    requiresAuth: true,
                    permission: 'ean_codes_compliance'
                }
            },
            {
                path: 'stop-cards',
                name: 'qualityStopCards',
                component: () => import('@/views/Quality/StopCards/Index.vue'),
                meta: {
                    title: 'quality.stopCards.pageTitle',
                    menuRendered: true,
                    menuTitle: 'quality.stopCards.menuTitle',
                    requiresAuth: true,
                    permission: 'stop_card'
                }
            },
            {
                path: 'certificate-approvals',
                name: 'qualityCertificateApprovals',
                component: () => import('@/views/Quality/CertificateApprovals/Index.vue'),
                meta: {
                    title: 'quality.certificateApprovals.pageTitle',
                    menuRendered: true,
                    menuTitle: 'quality.certificateApprovals.menuTitle',
                    requiresAuth: true,
                    permission: 'certificate_approvals'
                }
            },
            {
                path: 'certificate-approvals/create',
                name: 'qualityCertificateApprovalCreate',
                component: () => import('@/views/Quality/CertificateApprovals/Detail.vue'),
                beforeEnter: async () => {
                    const certificateApprovalsStore = useQualityCertificateApprovalsStore()
                    certificateApprovalsStore.clearCurrentApproval()
                    await prefetchCertificateApprovalLookups({ certificates: true })
                },
                meta: {
                    title: 'quality.certificateApprovals.create.pageTitle',
                    menuRendered: false,
                    requiresAuth: true,
                    permission: 'certificate_approvals'
                }
            },
            {
                path: 'certificate-approvals/:id',
                name: 'qualityCertificateApproval',
                component: () => import('@/views/Quality/CertificateApprovals/Detail.vue'),
                beforeEnter: createPrefetchGuard({
                    getId: (to) => to.params?.id,
                    fetch: async (id) => {
                        const certificateApprovalsStore = useQualityCertificateApprovalsStore()

                        await Promise.all([
                            certificateApprovalsStore.fetchCertificateApproval(id),
                            prefetchCertificateApprovalLookups()
                        ])

                        if (certificateApprovalsStore.currentApproval?.createdManually) {
                            await prefetchCertificateApprovalLookups({ papers: false, certificates: true })
                        }
                    },
                    onMissingId: () => ({ name: 'qualityCertificateApprovals' }),
                    onError: () => ({ name: 'qualityCertificateApprovals' })
                }),
                meta: {
                    title: 'quality.certificateApprovals.detail.pageTitle',
                    menuRendered: false,
                    requiresAuth: true,
                    permission: 'certificate_approvals'
                }
            }
        ]
    }
]
